/**
 * KpiOverview · 投放概览（KPI 汇总对比表）
 *
 * 接口契约：specs/多游戏投放对比页/api/overview.md
 *
 * 关键变更（接口契约对齐）：
 * - 4 列指标（增加 New Ads = new_creative）
 * - 行结构 `total` / `link_values` 嵌套：null 时显 '-'
 * - 服务端排序：sort_item ∈ {all_creative, new_creative, all_material, new_material}；默认 active_ads desc
 * - **前端分页**：接口返全量 ≤15 条，hook 内对 games 切片；TableCommonHook hasPageable=true
 * - max 改为 `{ game_id, value }`，进度条以 max.value 为分母
 * - **整体空态**：所有游戏 total 都为 null 时显示空态（无表格内容）
 *
 * 性能：
 * - intl 文案预先固化为 labels（避免 columns useMemo 依赖 intl 函数）
 * - GameCell 抽出 React.memo
 */
import React, { useMemo, useCallback } from 'react';
import CardCommon from '@op/components/CardCommon';
import TableCommonHook from '@op/components/TableCommonHook';
import ProgressColumn from '@op/pages/Overview/CompareKeyMetrics/component/ProgressColumn';
import { DBO_TM, DBO_V } from '@op/utils/format';
import useIntls from '@opm/hooks/useIntls';
import { IFilter } from '../../types/filter';
import { IKpiOverviewItem, TKpiSortItem } from '../../types/overview';
import { TSortOrder } from '../../types/common';
import { useKpiOverview } from '../../hooks/useKpiOverview';
import { useGameColorContext } from '../../context/GameColorContext';
import GameCell from './GameCell';
import './index.scss';
import BubbleCommon from '@op/components/BubbleCommon';
import { adCreativesComparisonOverviewTipsMap } from '@opm/components/BubbleCommon';
import {
  CARD_LABEL_MAP,
  getDayMode,
} from '@op/pages/adCreatives/overview/utils/metricLabels';

export const KPI_OVERVIEW_TITLE = 'KPI Overview';

export interface IKpiOverviewProps {
  filter: IFilter;
  /** DOM id（供父容器锚点定位） */
  id?: string;
  className?: string;
  emptyTitleText: string;
  gotoAdDeliveryOverview?: (id: string) => void;
}

/** 4 个可排序的指标字段（与接口 sort_item 白名单一致） */
type TKpiField = TKpiSortItem;

/** 表格行高（px） */
const ROW_HEIGHT = 72;
/** 表头高度（px） */
const HEADER_HEIGHT = 56;
/** 最小高度（px，loading / empty 占位） */
const MIN_HEIGHT = 320;

/** 计算 DOD 百分比 */
function calcRatio(current: number, previous: number): number {
  if (!previous) return 0;
  return ((current - previous) / previous) * 100;
}

export default function KpiOverview({
  filter,
  id,
  className,
  emptyTitleText,
  gotoAdDeliveryOverview,
}: IKpiOverviewProps) {
  const { intl } = useIntls();
  const { gameMap } = useGameColorContext();
  const {
    games,
    pagedGames,
    max,
    total,
    sort,
    setSort,
    pageInfo,
    setPageInfo,
    loading,
  } = useKpiOverview({ filter });

  /** 日期模式：单天 / 多天 */
  const dayMode = useMemo(
    () => getDayMode(filter.date_type, filter.start_time, filter.end_time),
    [filter.date_type, filter.start_time, filter.end_time],
  );

  /** 文案预先固化（避免 columns useMemo 依赖 intl 函数引用） */
  const labels = useMemo(
    () => ({
      game: intl('Game'),
      activeAds: intl(CARD_LABEL_MAP['Active Ads'][dayMode]),
      newAds: intl(CARD_LABEL_MAP['New Ads'][dayMode]),
      activeCreatives: intl(CARD_LABEL_MAP['Active Creatives'][dayMode]),
      newCreatives: intl(CARD_LABEL_MAP['New Creatives'][dayMode]),
      video: intl(CARD_LABEL_MAP['Active Video Ads'][dayMode]),
      image: intl(CARD_LABEL_MAP['Active Image Ads'][dayMode]),
      playable: intl(CARD_LABEL_MAP['Active Playable Ads'][dayMode]),
      releaseDate: intl('Release Date'),
      title: intl(KPI_OVERVIEW_TITLE),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [intl, dayMode],
  );

  /** 当前周期 / 环比期时间窗口（取首个非 null 行的 link_values.link_time） */
  const { newTime, oldTime } = useMemo(() => {
    const linkTime = games.find((g) => g.link_values?.link_time)?.link_values
      ?.link_time;
    if (!linkTime) {
      return {
        newTime: [filter?.start_time || '', filter?.end_time || ''],
        oldTime: ['', ''],
      };
    }
    return {
      newTime: [linkTime.new_start_date, linkTime.new_end_date] as string[],
      oldTime: [linkTime.old_start_date, linkTime.old_end_date] as string[],
    };
  }, [games, filter]);

  /** 单元格渲染工厂：返回针对指定 field 的 render 函数 */
  const renderProgress = useCallback(
    (field: TKpiField) => {
      function renderCell(row: IKpiOverviewItem) {
        // total = null（该游戏在区间内无数据）→ 显示 '-'
        if (!row.total) {
          return <span className="dbom-kpi-overview-empty">-</span>;
        }
        const value = row.total[field] as number;
        const oldValue = (row.link_values?.[field] as number) ?? 0;
        const totalValue = (max?.[field]?.value ?? 0) as number;
        return (
          <ProgressColumn
            percentage={calcRatio(value, oldValue)}
            value={value}
            oldValue={oldValue}
            totalValue={totalValue}
            showProgress
            newTime={newTime}
            oldTime={oldTime}
            formateValue={DBO_V.reviews}
            formateDate={(date: string) => DBO_TM.sFormat(date)}
          />
        );
      }
      return renderCell;
    },
    [max, newTime, oldTime],
  );

  /** 列配置 */
  const columns = useMemo(
    () => [
      {
        key: 'game',
        header: labels.game,
        width: 260,
        fixed: 'left' as const,
        render: (row: IKpiOverviewItem) => {
          const game = gameMap[row.game_id];
          return (
            <GameCell
              onClick={() => gotoAdDeliveryOverview?.(row.game_id)}
              gameName={game?.game_name || row.game_id}
              cover={game?.cover}
              releaseDate={game?.release_date}
              releaseDateLabel={labels.releaseDate}
            />
          );
        },
      },
      {
        key: 'all_creative',
        header: labels.activeAds,
        align: 'right' as const,
        width: 240,
        render: renderProgress('all_creative'),
      },
      {
        key: 'new_creative',
        header: labels.newAds,
        align: 'right' as const,
        width: 240,
        render: renderProgress('new_creative'),
      },
      {
        key: 'all_material',
        header: labels.activeCreatives,
        align: 'right' as const,
        width: 240,
        render: renderProgress('all_material'),
      },
      {
        key: 'new_material',
        header: labels.newCreatives,
        align: 'right' as const,
        width: 240,
        render: renderProgress('new_material'),
      },
      {
        key: 'creative_type_video',
        header: labels.video,
        align: 'right' as const,
        width: 240,
        render: renderProgress('creative_type_video'),
      },
      {
        key: 'creative_type_image',
        header: labels.image,
        align: 'right' as const,
        width: 240,
        render: renderProgress('creative_type_image'),
      },
      {
        key: 'creative_type_playable',
        header: labels.playable,
        align: 'right' as const,
        width: 240,
        render: renderProgress('creative_type_playable'),
      },
    ],
    [labels, renderProgress, gameMap],
  );

  /**
   * 适配 TableCommonHook 的 sortInfo 形态：[sortBy, setSortBy]
   * - 默认排序 active_ads desc（hook 初始值已设置）
   * - 表头点击切换 by / order；点击后再次点击同列由 tea-component 内部翻转 order
   */
  const sortInfo = useMemo<[any, (v: any) => void]>(() => {
    const sortBy = {
      by: sort.item,
      order: sort.model,
    };
    return [
      sortBy,
      (next: any) => {
        if (!next?.by) return;
        setSort({
          item: next.by as TKpiSortItem,
          model: (next.order as TSortOrder) || 'desc',
        });
      },
    ];
  }, [sort, setSort]);

  /** 表格高度：基于"当前页行数" + 表头（避免空数据时高度过高） */
  const tableHeight = useMemo(
    () => Math.max(MIN_HEIGHT, HEADER_HEIGHT + pagedGames.length * ROW_HEIGHT),
    [pagedGames.length],
  );

  /** recordKey 稳定引用 */
  const recordKey = useMemo(() => (r: IKpiOverviewItem) => r.game_id, []);

  return (
    <CardCommon
      id={id}
      className={`dbom-kpi-overview-card ${className ?? ''}`}
      title={
        <BubbleCommon
          tipsMap={adCreativesComparisonOverviewTipsMap}
          className="dbom-kpi-overview-bubble"
        >
          {labels.title}
        </BubbleCommon>
      }
      loading={loading}
      loadingHeight={MIN_HEIGHT}
    >
      <TableCommonHook
        className="dbom-kpi-overview-table"
        records={pagedGames}
        columns={columns}
        recordKey={recordKey}
        sortColumns={[
          'all_creative',
          'new_creative',
          'all_material',
          'new_material',
          'creative_type_video',
          'creative_type_image',
          'creative_type_playable',
        ]}
        sortInfo={sortInfo}
        sortType="server"
        total={total}
        pageInfo={pageInfo}
        setPageInfo={setPageInfo}
        defaultPageInfo={{ page: 1, page_size: 10 }}
        height={tableHeight}
        bordered={'all'}
        emptyProps={{ emptyTitleText, hasDesc: false }}
      />
    </CardCommon>
  );
}
